import React, { useMemo, useState, useEffect } from "react";
import { Search, X, Check, Globe, Users } from "lucide-react";

/**
 * Reusable client-target picker.
 * Props:
 *   pros: [{id, raison_sociale, prenom, nom, email}, ...]
 *   value: string[]  (selected user ids)
 *   onChange: (nextIds: string[]) => void
 *   compact?: boolean  (renders a smaller card fitting in forms)
 */
export default function ClientTargetPicker({ pros, value = [], onChange, compact = false }) {
    const [q, setQ] = useState("");
    const selectedSet = useMemo(() => new Set(value), [value]);

    const normalize = (s) =>
        (s || "")
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const filtered = useMemo(() => {
        const needle = normalize(q.trim());
        const list = [...(pros || [])].sort((a, b) =>
            (a.raison_sociale || "").localeCompare(b.raison_sociale || "", "fr", { sensitivity: "base" })
        );
        if (!needle) return list;
        return list.filter((p) => {
            const hay = normalize(`${p.raison_sociale} ${p.prenom} ${p.nom} ${p.email}`);
            return hay.includes(needle);
        });
    }, [pros, q]);

    const toggle = (id) => {
        const next = new Set(selectedSet);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChange(Array.from(next));
    };

    const clearAll = () => onChange([]);

    return (
        <div className={compact ? "space-y-3" : "space-y-4"} data-testid="client-target-picker">
            <div
                className="p-3 border flex items-start gap-3"
                style={{
                    borderColor: value.length ? "var(--bc-gold)" : "var(--bc-border)",
                    background: value.length ? "rgba(212,188,124,0.06)" : "rgba(255,255,255,0.02)",
                }}
            >
                {value.length === 0 ? (
                    <>
                        <Globe size={16} className="text-[#A0A0A0] mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm text-[#F2ECE0]">Article public</p>
                            <p className="text-xs text-[#A0A0A0] mt-1">
                                Visible par tous les professionnels validés. Sélectionnez un ou plusieurs clients ci-dessous pour restreindre.
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                        <Users size={16} className="text-[#D4BC7C] mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm text-[#D4BC7C]">
                                Article exclusif — {value.length} client{value.length > 1 ? "s" : ""} sélectionné{value.length > 1 ? "s" : ""}
                            </p>
                            <p className="text-xs text-[#A0A0A0] mt-1">
                                Visible uniquement par les clients cochés et par l&apos;admin.
                            </p>
                            <button
                                type="button"
                                className="text-[10px] text-[#D4BC7C] uppercase tracking-widest mt-2 hover:underline"
                                onClick={clearAll}
                                data-testid="target-clear-all"
                            >
                                Effacer la sélection · Rendre public
                            </button>
                        </div>
                    </>
                )}
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4BC7C] pointer-events-none z-10" size={14} />
                <input
                    className="bc-input"
                    style={{ paddingLeft: "2.25rem" }}
                    placeholder="Rechercher un client (raison sociale, nom, email)…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    data-testid="target-search"
                />
            </div>

            <div
                className="max-h-64 overflow-y-auto border divide-y"
                style={{ borderColor: "var(--bc-border)", background: "#0a0a0a" }}
            >
                {filtered.length === 0 && (
                    <p className="text-xs text-[#A0A0A0] p-3">Aucun client validé trouvé.</p>
                )}
                {filtered.map((p) => {
                    const on = selectedSet.has(p.id);
                    return (
                        <button
                            type="button"
                            key={p.id}
                            onClick={() => toggle(p.id)}
                            className="w-full flex items-start gap-3 p-3 text-left hover:bg-[rgba(212,188,124,0.05)] transition-colors"
                            data-testid={`target-option-${p.id}`}
                        >
                            <div
                                className="w-4 h-4 border flex items-center justify-center shrink-0 mt-0.5"
                                style={{
                                    borderColor: on ? "var(--bc-gold)" : "var(--bc-border)",
                                    background: on ? "var(--bc-gold)" : "transparent",
                                }}
                            >
                                {on && <Check size={12} className="text-[#000]" strokeWidth={3} />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-[#F2ECE0] truncate">
                                    {p.raison_sociale || `${p.prenom || ""} ${p.nom || ""}`.trim() || p.email}
                                </p>
                                <p className="text-xs text-[#A0A0A0] truncate">
                                    {p.prenom} {p.nom} · {p.email}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Full modal wrapper. Use for existing-item edit.
 */
export function ClientTargetModal({ open, onClose, pros, initialValue, onSave, itemLabel }) {
    const [value, setValue] = useState(initialValue || []);

    useEffect(() => {
        if (open) setValue(initialValue || []);
    }, [open, initialValue]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
            data-testid="client-target-modal"
        >
            <div
                className="bc-card w-full max-w-lg max-h-[90vh] flex flex-col"
                style={{ padding: "1.5rem", background: "rgba(15,15,15,0.98)" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <p className="bc-label">Ciblage clients</p>
                        {itemLabel && (
                            <p className="text-sm text-[#F2ECE0] mt-1 line-clamp-1" title={itemLabel}>
                                {itemLabel}
                            </p>
                        )}
                    </div>
                    <button
                        className="text-[#A0A0A0] hover:text-[#D4BC7C]"
                        onClick={onClose}
                        data-testid="target-modal-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-4 flex-1 overflow-y-auto">
                    <ClientTargetPicker pros={pros} value={value} onChange={setValue} compact />
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                    <button className="bc-btn bc-btn--sm bc-btn--ghost" onClick={onClose}>
                        Annuler
                    </button>
                    <button
                        className="bc-btn bc-btn--sm"
                        onClick={() => onSave(value)}
                        data-testid="target-modal-save"
                    >
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}
